import Routes from "./routes";
import { LoginContext } from "./context/LoginContext";
import { useCallback, useEffect, useRef, useState } from "react";
import api, {
  markSessionActive,
  subscribeToSessionLoss,
} from "./services/api";
import {
  bootstrapStoredSession,
  clearStoredToken,
  getStoredToken,
  getStudentAccesses,
  normalizeAuthenticatedUser,
  persistRefreshToken,
  persistToken,
  reconcileActiveStudentId,
  SESSION_STATUS,
} from "./auth/session";

export default function App() {
  const [initialSession] = useState(() => bootstrapStoredSession());
  const [LoggedUser, setLoggedUser] = useState(initialSession.user);
  const [Logged_in, setLogged_in] = useState(initialSession.loggedIn);
  const [token, setToken] = useState(initialSession.token);
  const [sessionStatus, setSessionStatus] = useState(initialSession.status);
  const [activeStudentId, setActiveStudentId] = useState("");
  const sessionOperationRef = useRef(0);
  const bootstrapRequestRef = useRef(null);
  const logoutRequestRef = useRef(null);

  const setLostSession = useCallback(() => {
    setLoggedUser(null);
    setLogged_in(false);
    setToken("");
    setActiveStudentId("");
    setSessionStatus(SESSION_STATUS.LOST);
    clearStoredToken();
    delete api.defaults.headers.common.Authorization;
  }, []);

  const setAuthenticatedSession = useCallback(
    (user, fallbackToken = "") => {
      const normalizedUser = normalizeAuthenticatedUser(user);

      if (!normalizedUser) {
        setLostSession();
        return false;
      }

      markSessionActive();
      setLoggedUser(normalizedUser);
      setActiveStudentId((currentStudentId) =>
        reconcileActiveStudentId(
          getStudentAccesses(normalizedUser),
          currentStudentId,
        ),
      );
      setLogged_in(true);
      setToken(getStoredToken() || fallbackToken);
      setSessionStatus(SESSION_STATUS.AUTHENTICATED);
      return true;
    },
    [setLostSession],
  );

  const selectActiveStudent = useCallback(
    (studentId) => {
      const nextStudentId = reconcileActiveStudentId(
        getStudentAccesses(LoggedUser),
        studentId,
      );

      setActiveStudentId(nextStudentId);
      return nextStudentId;
    },
    [LoggedUser],
  );

  const reconcileStudentContext = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      const normalizedUser = normalizeAuthenticatedUser(response.data);

      if (!normalizedUser) {
        setLostSession();
        return [];
      }

      setAuthenticatedSession(normalizedUser, getStoredToken() || token);
      return getStudentAccesses(normalizedUser);
    } catch {
      setLostSession();
      return [];
    }
  }, [setAuthenticatedSession, setLostSession, token]);

  useEffect(
    () =>
      subscribeToSessionLoss(() => {
        sessionOperationRef.current += 1;
        setLostSession();
      }),
    [setLostSession],
  );

  useEffect(() => {
    if (token) {
      markSessionActive();
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      persistToken(token);
      return;
    }

    delete api.defaults.headers.common.Authorization;
    clearStoredToken();
  }, [token]);

  useEffect(() => {
    if (initialSession.status !== SESSION_STATUS.LOADING) {
      return undefined;
    }

    let active = true;
    const operationId = ++sessionOperationRef.current;
    bootstrapRequestRef.current =
      bootstrapRequestRef.current || api.get("/auth/me");

    bootstrapRequestRef.current
      .then((response) => {
        if (active && sessionOperationRef.current === operationId) {
          setAuthenticatedSession(response.data, initialSession.token);
        }
      })
      .catch(() => {
        if (active && sessionOperationRef.current === operationId) {
          setLostSession();
        }
      });

    return () => {
      active = false;
    };
  }, [initialSession, setAuthenticatedSession, setLostSession]);

  const signIn = async (sessionOrToken) => {
    const jwt =
      typeof sessionOrToken === "string"
        ? sessionOrToken
        : sessionOrToken?.authToken || sessionOrToken?.token;

    if (!jwt) {
      sessionOperationRef.current += 1;
      setLostSession();
      return false;
    }

    const operationId = ++sessionOperationRef.current;
    setLoggedUser(null);
    setLogged_in(false);
    setSessionStatus(SESSION_STATUS.LOADING);
    setToken(jwt);
    persistToken(jwt);
    persistRefreshToken(
      typeof sessionOrToken === "string" ? "" : sessionOrToken?.refreshToken,
    );
    markSessionActive();
    api.defaults.headers.common.Authorization = `Bearer ${jwt}`;

    try {
      const response = await api.get("/auth/me");

      if (sessionOperationRef.current !== operationId) {
        return false;
      }

      return setAuthenticatedSession(response.data, jwt);
    } catch {
      if (sessionOperationRef.current === operationId) {
        setLostSession();
      }

      return false;
    }
  };

  const signOut = useCallback(() => {
    if (logoutRequestRef.current) {
      return logoutRequestRef.current;
    }

    const currentToken = getStoredToken() || token;
    const operationId = ++sessionOperationRef.current;

    if (!currentToken) {
      setLostSession();
      return Promise.resolve(true);
    }

    setSessionStatus(SESSION_STATUS.LOADING);

    const logoutRequest = api
      .post("/auth/logout")
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        if (logoutRequestRef.current === logoutRequest) {
          logoutRequestRef.current = null;
        }

        if (sessionOperationRef.current === operationId) {
          setLostSession();
        }
      });

    logoutRequestRef.current = logoutRequest;
    return logoutRequest;
  }, [setLostSession, token]);

  return (
    <LoginContext.Provider value={
      { 
        LoggedUser, 
        setLoggedUser, 
        Logged_in, 
        setLogged_in,
        token,
        setToken,
        sessionStatus,
        sessionLoading: sessionStatus === SESSION_STATUS.LOADING,
        studentAccesses: getStudentAccesses(LoggedUser),
        activeStudentId,
        selectActiveStudent,
        reconcileStudentContext,
        signIn,
        signOut,
      }
    }>
      <Routes />
    </LoginContext.Provider>
  );
}
